import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import StatusPill from "../../components/common/StatusPill";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { invalidateMembersList } from "../../store/slices/membersSlice";
import {
  fetchOwnerCertificationDetail,
  fetchOwnerCertifications,
  updateOwnerCertificationStatus,
} from "../../store/slices/ownerCertificationsSlice";
import { ReviewStatus } from "../../types/admin";
import { formatDate, reviewStatusLabelMap, roleLabelMap } from "../../utils/admin";

const OwnerCertificationDetailPage = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { detail, loadingDetail, detailError, updating, updateError } = useAppSelector(
    (state) => state.ownerCertifications
  );

  const [selectedStatus, setSelectedStatus] = useState<ReviewStatus>("pending");
  const [reviewNote, setReviewNote] = useState("");

  useEffect(() => {
    if (branchId) {
      dispatch(fetchOwnerCertificationDetail(Number(branchId)));
    }
  }, [branchId, dispatch]);

  useEffect(() => {
    if (detail) {
      setSelectedStatus(detail.review_status);
      setReviewNote(detail.review_note ?? "");
    }
  }, [detail]);

  const onSave = async () => {
    if (!branchId) {
      return;
    }

    if (selectedStatus === "rejected" && !reviewNote.trim()) {
      window.alert("반려 사유를 입력해 주세요.");
      return;
    }

    try {
      await dispatch(
        updateOwnerCertificationStatus({
          branchId: Number(branchId),
          review_status: selectedStatus,
          review_note: reviewNote || undefined,
        })
      ).unwrap();
      dispatch(invalidateMembersList());
      await dispatch(fetchOwnerCertificationDetail(Number(branchId)));
      dispatch(fetchOwnerCertifications({ page: 1, page_size: 20 }));
      window.alert(
        "사업주 인증 상태가 저장되었습니다.\n인증 완료 → 회원 「허용」 / 반려 → 「반려」 로 동기화됩니다."
      );
    } catch (error) {
      window.alert((error as string) || "상태 변경에 실패했습니다.");
    }
  };

  return (
    <section className="admin-page">
      <header className="detail-header">
        <button className="back-btn" type="button" onClick={() => navigate(-1)}>
          ←
        </button>
        <h2 className="section-title">사업주 인증 상세정보</h2>
      </header>

      {loadingDetail && <p className="loading-text">인증 요청 상세를 불러오는 중입니다...</p>}
      {detailError && <p className="error-text">{detailError}</p>}
      {updateError && <p className="error-text">{updateError}</p>}

      {detail && (
        <>
          <h3 className="section-title">요청인 정보</h3>
          <div className="detail-grid">
            <article className="detail-card">
              <span>이름</span>
              <strong>{detail.user_name}</strong>
            </article>
            <article className="detail-card">
              <span>이메일</span>
              <strong>{detail.email}</strong>
            </article>
            <article className="detail-card">
              <span>연락처</span>
              <strong>{detail.phone_number}</strong>
            </article>
            <article className="detail-card">
              <span>분류</span>
              <strong>{roleLabelMap[detail.role]}</strong>
            </article>
            <article className="detail-card">
              <span>회사명</span>
              <strong>{detail.branch_name}</strong>
            </article>
            <article className="detail-card">
              <span>사업자 등록 번호</span>
              <strong>{detail.business_number}</strong>
            </article>
            <article className="detail-card">
              <span>요청일</span>
              <strong>{formatDate(detail.created_at)}</strong>
            </article>
            <article className="detail-card">
              <span>현재 인증 상태</span>
              <strong>{reviewStatusLabelMap[detail.review_status] || detail.review_status}</strong>
            </article>
          </div>

          <h3 className="section-title">인증 상태</h3>
          <p className="owner-cert-sync-hint">
            인증 완료 시 사업주 회원 상태는 「허용」, 반려 시 「반려」, 대기 시 「심사 대기」로 맞춰집니다.
          </p>
          <div className="status-group">
            <StatusPill label="인증 대기" active={selectedStatus === "pending"} onClick={() => setSelectedStatus("pending")} />
            <StatusPill label="인증 완료" active={selectedStatus === "approved"} onClick={() => setSelectedStatus("approved")} />
            <StatusPill label="반려" active={selectedStatus === "rejected"} onClick={() => setSelectedStatus("rejected")} />
          </div>

          <div className="textarea-wrap">
            <label htmlFor="review-note">
              {selectedStatus === "rejected" ? "반려 사유" : "검토 메모"}
              {selectedStatus === "rejected" && <span style={{ color: "var(--danger)", marginLeft: "4px" }}>*</span>}
            </label>
            <textarea
              id="review-note"
              placeholder={selectedStatus === "rejected" ? "반려 사유를 입력해 주세요." : "검토 메모를 입력해 주세요."}
              value={reviewNote}
              onChange={(event) => setReviewNote(event.target.value)}
            />
          </div>

          <div className="detail-actions">
            <button className="primary-btn" type="button" onClick={onSave} disabled={updating}>
              {updating ? "저장 중..." : "인증 상태 저장"}
            </button>
          </div>
        </>
      )}
    </section>
  );
};

export default OwnerCertificationDetailPage;
